<template>
  <admin-layout :title="$t('Settings')">
    <tec-form-section @submitted="company">
      <template #title>{{ $t('Company') }}</template>
      <template #description>{{ $t('Please fill the form below to update company details.') }}</template>

      <template #form>
        <div class="col-span-full flex items-start flex-wrap -mr-4 md:-mr-6 md:rounded-md">
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.company_name" :error="$page.props.errors.company_name" :label="$t('Company Name')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.email" :error="$page.props.errors.email" :label="$t('Company Email')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.phone" :error="$page.props.errors.phone" :label="$t('Company Phone')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.gst" :error="$page.props.errors.gst" :label="$t('GST')" />
            </div>
          </div>
          <div class="w-full">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.address" :error="$page.props.errors.address" :label="$t('Address')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.city" :error="$page.props.errors.city" :label="$t('City')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.state" :error="$page.props.errors.state" :label="$t('State')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.postal_code" :error="$page.props.errors.postal_code" :label="$t('Postal Code')" />
            </div>
          </div>
          <div class="w-full sm:w-1/2">
            <div class="mr-6 mb-6">
              <text-input v-model="aform.country" :error="$page.props.errors.country" :label="$t('Country')" />
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <tec-action-message :on="form.recentlySuccessful" class="mr-3"> Saved. </tec-action-message>
        <loading-button type="submit" :loading="form.processing" :disabled="form.processing"> Save </loading-button>
      </template>
    </tec-form-section>

    <div class="mt-8">
      <tec-form-section @submitted="upload">
        <template #title>{{ $t('Logos') }}</template>
        <template #description>{{ $t('Please select logo to upload.') }}</template>

        <template #form>
          <div class="col-span-full flex items-start flex-wrap -mr-4 md:-mr-6 md:rounded-md">
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <!-- Profile Photo File Input -->
                <input ref="loginLogoInput" type="file" class="hidden" @change="updateLoginLogoPreview" />
                <JetLabel for="loginLogo" :value="$t('Login Logo')" />
                <!-- Current Profile Photo -->
                <div v-show="!loginLogoPreview && current.login_logo_url" class="my-2 max-h-40 w-80 max-w-full">
                  <img :src="current.login_logo_url" :alt="current.name" class="max-h-full max-w-full rounded object-cover" />
                </div>
                <!-- New Profile Photo Preview -->
                <div v-show="loginLogoPreview" class="my-2 max-h-40 w-80 max-w-full">
                  <img :src="loginLogoPreview" :alt="current.name" class="max-h-full max-w-full rounded object-cover" />
                </div>
                <p v-show="loginLogoPreview" class="text-red-500">{{ $t('Logo not yet saved') }}</p>
                <JetSecondaryButton class="mt-2 mr-2" type="button" @click.prevent="selectNewLoginLogo">
                  {{ $t('Select Logo') }}
                </JetSecondaryButton>
                <JetInputError :message="form.errors.login_logo" class="mt-2" />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <input ref="invoiceLogoInput" type="file" class="hidden" @change="updateInvoiceLogoPreview" />
                <JetLabel for="invoiceLogo" :value="$t('Invoice Logo')" />
                <div v-show="!invoiceLogoPreview && current.invoice_logo_url" class="my-2 max-h-40 w-80 max-w-full">
                  <img :src="current.invoice_logo_url" :alt="current.name" class="max-h-full max-w-full rounded object-cover" />
                </div>
                <div v-show="invoiceLogoPreview" class="my-2 max-h-40 w-80 max-w-full">
                  <img :src="invoiceLogoPreview" :alt="current.name" class="max-h-full max-w-full rounded object-cover" />
                </div>
                <p v-show="invoiceLogoPreview" class="text-red-500">{{ $t('Logo not yet saved') }}</p>
                <JetSecondaryButton class="mt-2 mr-2" type="button" @click.prevent="selectNewInvoiceLogo">
                  {{ $t('Select Logo') }}
                </JetSecondaryButton>
                <JetInputError :message="form.errors.invoice_logo" class="mt-2" />
              </div>
            </div>
          </div>
        </template>

        <template #actions>
          <tec-action-message :on="form.recentlySuccessful" class="mr-3"> Saved. </tec-action-message>
          <loading-button type="submit" :loading="form.processing" :disabled="form.processing"> Save </loading-button>
        </template>
      </tec-form-section>
    </div>

    <div class="mt-8">
      <tec-form-section @submitted="submit">
        <template #title>{{ $t('Settings') }}</template>
        <template #description>{{ $t('Please fill the form below to update settings.') }}</template>

        <template #form>
          <div class="col-span-full flex items-start flex-wrap -mr-4 md:-mr-6 md:rounded-md">
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input v-model="form.name" :error="$page.props.errors.name" :label="$t('Site Name')" />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input v-model="form.currency_code" :error="$page.props.errors.currency_code" :label="$t('Currency Code')" />
              </div>
            </div>

            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <select-input v-model="form.per_page" :error="$page.props.errors.per_page" :label="$t('Rows per page')">
                  <option value="">{{ $t('Select') }}</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </select-input>
              </div>
            </div>
            <!-- <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <select-input v-model="form.default_tax_rate_id" :error="$page.props.errors.default_tax_rate_id" :label="$t('Tax Rate')">
                  <option value="">{{ $t('Select') }}</option>
                  <option :value="tax_rate.id" v-for="tax_rate of tax_rates" :key="tax_rate.id">{{ tax_rate.name }}</option>
                </select-input>
              </div>
            </div> -->

            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <text-input v-model="form.default_locale" :error="$page.props.errors.default_locale" :label="$t('Date & Number Locale')" />
              </div>
            </div>
            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <select-input v-model="form.fraction" :error="$page.props.errors.fraction" :label="$t('Decimal Fractions')">
                  <option value="">{{ $t('Select') }}</option>
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select-input>
              </div>
            </div>

            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <select-input v-model="form.sidebar" :error="$page.props.errors.sidebar" :label="$t('Sidebar')">
                  <option value="">{{ $t('Select') }}</option>
                  <option value="full">{{ $t('Default') }}</option>
                  <option value="mini">{{ $t('Collapsed') }}</option>
                </select-input>
              </div>
            </div>
            <transition name="slidedown">
              <div class="w-full sm:w-1/2" v-if="form.sidebar == 'full'">
                <div class="mr-6 mb-6">
                  <select-input v-model="form.sidebar_style" :error="$page.props.errors.sidebar_style" :label="$t('Sidebar Style')">
                    <option value="">{{ $t('Select') }}</option>
                    <option value="full">{{ $t('Full') }}</option>
                    <option value="dropdown">{{ $t('Dropdown') }}</option>
                  </select-input>
                </div>
              </div>
            </transition>

            <div class="w-full sm:w-1/2">
              <div class="mr-6 mb-6">
                <select-input v-model="form.modal_position" :error="$page.props.errors.modal_position" :label="$t('Modal Position')">
                  <option value="top">{{ $t('Top') }}</option>
                  <option value="center">{{ $t('Center') }}</option>
                </select-input>
              </div>
            </div>

            <div class="w-full">
              <div class="mr-6 mb-6">
                <label for="default_sale_comment" class="block text-sm font-medium text-gray-700">{{ $t('Default Sale Comment') }}</label>
                <div class="mt-1">
                  <textarea
                    rows="4"
                    id="default_sale_comment"
                    v-model="form.default_sale_comment"
                    class="shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 block w-full sm:text-sm border-gray-300 rounded-md"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #actions>
          <tec-action-message :on="form.recentlySuccessful" class="mr-3"> Saved. </tec-action-message>
          <loading-button type="submit" :loading="form.processing" :disabled="form.processing"> Save </loading-button>
        </template>
      </tec-form-section>
    </div>

    <!-- This example requires Tailwind CSS v2.0+ -->
    <div class="mt-8">
      <tec-form-section>
        <template #title>{{ $t('Preview') }}</template>
        <template #description>{{ $t('Number, Currency and Date Format') }}</template>

        <template #form>
          <div class="col-span-full -m-6 bg-white shadow overflow-hidden md:rounded-md">
            <!-- <div class="px-4 py-5 sm:px-6 border-b">
          <h3 class="text-lg leading-6 font-medium text-gray-900">{{ $t('Preview') }}</h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-500">{{ $t('Number, Currency and Date Format') }}</p>
        </div> -->
            <div class="border-gray-200">
              <dl>
                <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">{{ $t('Number') }}</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{
                      $number(20000000.5, this.form.default_locale, {
                        minimumFractionDigits: this.form.fraction,
                      })
                    }}
                  </dd>
                </div>
                <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">{{ $t('Currency') }}</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{
                      $currency(20000000.5, this.form.default_locale, {
                        style: 'currency',
                        currencyDisplay: 'symbol',
                        currency: this.form.currency_code,
                        minimumFractionDigits: this.form.fraction,
                      })
                    }}
                  </dd>
                </div>
                <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">{{ $t('Date') }}</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{ $date(new Date(), this.form.default_locale) }}
                  </dd>
                </div>
                <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">{{ $t('Date Time') }}</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{ $datetime(new Date(), this.form.default_locale) }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </template>
      </tec-form-section>
    </div>
  </admin-layout>
</template>

<script>
import TextInput from '@/Shared/TextInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import LoadingButton from '@/Shared/LoadingButton';
import TecFormSection from '@/Jetstream/FormSection';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';

export default {
  props: ['current', 'tax_rates', 'account'],

  components: {
    TextInput,
    SelectInput,
    AdminLayout,
    LoadingButton,
    TecFormSection,
    TecSectionTitle,
    TecActionMessage,
    JetInputError,
    JetLabel,
    JetSecondaryButton,
  },

  data() {
    return {
      loginLogoInput: null,
      loginLogoPreview: null,
      invoiceLogoInput: null,
      invoiceLogoPreview: null,
      form: this.$inertia.form({
        _method: 'POST',
        name: this.current.name,
        sidebar: this.current.sidebar,
        fraction: this.current.fraction,
        per_page: this.current.per_page || 10,
        currency_code: this.current.currency_code,
        sidebar_style: this.current.sidebar_style,
        default_locale: this.current.default_locale,
        modal_position: this.current.modal_position,
        default_tax_rate_id: this.current.default_tax_rate_id,
        default_sale_comment: this.current.default_sale_comment,
      }),

      aform: this.$inertia.form({
        _method: 'POST',
        company_name: this.account.company_name,
        email: this.account.email,
        phone: this.account.phone,
        gst: this.account.gst,
        address: this.account.address,
        city: this.account.city,
        state: this.account.state,
        postal_code: this.account.postal_code,
        country: this.account.country,
      }),
    };
  },

  methods: {
    company() {
      this.aform.post(route('settings.company.update'), { preserveScroll: true });
    },
    submit() {
      this.form.post(route('settings.store'), { preserveScroll: true });
    },
    upload() {
      console.log(this.loginLogoInput, this.invoiceLogoInput);
      if (this.loginLogoInput || this.invoiceLogoInput) {
        const form = this.$inertia.form({
          login_logo: this.loginLogoInput,
          invoice_logo: this.invoiceLogoInput,
        });
        form.post(route('settings.upload'), {
          preserveScroll: true,
          forceFormData: true,
          onSuccess: () => {
            this.loginLogoPreview = null;
            this.invoiceLogoPreview = null;
          },
        });
      }
    },

    selectNewLoginLogo() {
      this.$refs.loginLogoInput.click();
    },
    updateLoginLogoPreview() {
      console.log(this.$refs.loginLogoInput.value);
      const logo = this.$refs.loginLogoInput.files[0];
      this.loginLogoInput = this.$refs.loginLogoInput.files[0];
      if (!logo) return;
      const reader = new FileReader();
      reader.onload = e => {
        this.loginLogoPreview = e.target.result;
      };
      reader.readAsDataURL(logo);
    },
    selectNewInvoiceLogo() {
      this.$refs.invoiceLogoInput.click();
    },
    updateInvoiceLogoPreview() {
      console.log(this.$refs.invoiceLogoInput.value);
      const logo = this.$refs.invoiceLogoInput.files[0];
      this.invoiceLogoInput = this.$refs.invoiceLogoInput.files[0];
      if (!logo) return;
      const reader = new FileReader();
      reader.onload = e => {
        this.invoiceLogoPreview = e.target.result;
      };
      reader.readAsDataURL(logo);
    },
  },
};
</script>
